import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./MessageMonitor.module.scss";
import avatarPng from "@/assets/UI/avatar.png";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";

const PAGE_SIZE = 50;


const safeStr = (value) => (value == null ? "" : String(value));

const buildVideoMeta = (att) => {
  if (!att) return null;
  return {
    posterUrl: safeStr(att.posterUrl || "").trim(),
    hlsUrl: safeStr(att.hlsMasterUrl || att.url || "").trim(),
    videoId: safeStr(att.videoId || "").trim(),
  };
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatRelative = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = date.getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  const minutes = Math.round(diffMs / (1000 * 60));
  const hours = Math.round(diffMs / (1000 * 60 * 60));
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (Math.abs(minutes) < 60) return rtf.format(minutes, "minute");
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
  return rtf.format(days, "day");
};

const MessageStatusBadge = ({ status }) => {
  const normalized = String(status || "sent").toLowerCase();

  return (
    <span className={`${cls.status} ${cls[normalized] || ""}`}>
      <span className={cls.statusDot} />
      {normalized}
    </span>
  );
};

const UserMiniCard = ({ user, fallbackAlt, who }) => {
  const username = user?.username || "Unknown";
  const avatarSrc = user?.avatarUrl || avatarPng;

  return (
    <div className={cls.userCard}>
      <img
        src={avatarSrc}
        alt={fallbackAlt || username}
        className={cls.avatar}
      />
      <div className={cls.userMeta}>
        <div className={cls.userName}>{username}</div>
        <div className={cls.userSub}>{who}</div>
      </div>
    </div>
  );
};

const MediaBlock = ({ message, accessToken }) => {
  const attachments = Array.isArray(message.attachments)
    ? message.attachments
    : [];
  const hasText = Boolean(String(message?.text || "").trim());

  if (!attachments.length) {
    return <p className={cls.messageText}>{hasText ? message.text : "—"}</p>;
  }

  return (
    <div className={cls.mediaBlock}>
      {hasText ? <p className={cls.messageText}>{message.text}</p> : null}

      <div className={cls.mediaGrid}>
        {attachments.map((att, idx) => {
          if (att?.type === "video") {
             const meta = buildVideoMeta(att);
            const canProxyPlay = Boolean(meta?.videoId && accessToken);
            const canDirectPlay = Boolean(meta?.hlsUrl);

            return (
              <div key={`${message.id}-video-${idx}`} className={cls.videoWrap}>
                <div className={cls.mediaType}>Video</div>
                  {canProxyPlay ? (
                  <VideoPlayer
                    token={accessToken}
                    videoId={meta.videoId}
                    mode="proxy"
                    poster={meta.posterUrl || undefined}
                    autoPlay={false}
                    muted
                    controls
                    className={cls.videoPlayer}
                  />
                ) : canDirectPlay ? (
                  <video
                    className={cls.videoPlayer}
                    src={meta.hlsUrl}
                    poster={meta.posterUrl || undefined}
                    controls
                    preload="metadata"
                    playsInline
                    controlsList="nodownload"
                  />
                ) : (
                  <p className={cls.messageText}>Processing video... Please wait.</p>
                )}
              </div>
            );
          }

          const imageUrl =
            att?.variants?.find((variant) => variant?.url)?.url ||
            att?.url ||
            "";

          if (!imageUrl) return null;

          return (
            <div key={`${message.id}-image-${idx}`} className={cls.photoCard}>
              <div className={cls.mediaType}>Photo</div>
              <img
                src={imageUrl}
                alt="Message media"
                className={cls.photo}
                loading="lazy"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function MessageMonitor() {
    const accessToken = useMemo(() => {
    try {
      return localStorage.getItem("cm_at");
    } catch {
      return null;
    }
  }, []);
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    totalPages: 1,
    total: 0,
    hasPrevPage: false,
    hasNextPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchMessages = useCallback(async (pageNumber = 1) => {
    try {
      setLoading(true);
      setError("");

      const { data } = await api.get("/v1/admin/chat/monitor/messages", {
        params: { page: pageNumber },
      });

      setMessages(data?.items || []);
      setPage(data?.page || pageNumber);
      setMeta({
        totalPages: data?.totalPages || 1,
        total: data?.total || 0,
        hasPrevPage: data?.hasPrevPage || false,
        hasNextPage: data?.hasNextPage || false,
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load messages.");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages(1);
  }, [fetchMessages]);

  const rows = useMemo(() => messages || [], [messages]);

  const sentCount = useMemo(
    () =>
      rows.filter((item) => String(item?.status || "").toLowerCase() === "sent")
        .length,
    [rows],
  );

  const deliveredCount = useMemo(
    () =>
      rows.filter(
        (item) => String(item?.status || "").toLowerCase() === "delivered",
      ).length,
    [rows],
  );

  const readCount = useMemo(
    () =>
      rows.filter((item) => String(item?.status || "").toLowerCase() === "read")
        .length,
    [rows],
  );

  return (
    <div className={cls.page}>
      <header className={cls.hero}>
        <div className={cls.heroText}>
          <span className={cls.eyebrow}>Admin chat analytics</span>
          <h1>Message Monitor</h1>
          <p>
            Review sent messages, media, delivery status, and activity timeline
            in one clean view.
          </p>
        </div>

        <div className={cls.heroStats}>
          <div className={cls.statCard}>
            <span className={cls.statLabel}>Total records</span>
            <strong>{meta.total}</strong>
          </div>
          <div className={cls.statCard}>
            <span className={cls.statLabel}>This page</span>
            <strong>{rows.length}</strong>
          </div>
          <div className={cls.statCard}>
            <span className={cls.statLabel}>Per page</span>
            <strong>{PAGE_SIZE}</strong>
          </div>
        </div>
      </header>

      <nav className={cls.tabs}>
        <Link to="/admin" className={cls.tabButton}>
          Dashboard
        </Link>
        <Link to="/admin/chat" className={cls.tabButton}>
          Chat
        </Link>
        <Link
          to="/admin/chat/monitor"
          className={`${cls.tabButton} ${cls.active}`}
        >
          Message Monitor
        </Link>
      </nav>

      <section className={cls.summaryRow}>
        <div className={cls.summaryCard}>
          <span className={cls.summaryTitle}>Sent</span>
          <strong>{sentCount}</strong>
        </div>
        <div className={cls.summaryCard}>
          <span className={cls.summaryTitle}>Delivered</span>
          <strong>{deliveredCount}</strong>
        </div>
        <div className={cls.summaryCard}>
          <span className={cls.summaryTitle}>Read</span>
          <strong>{readCount}</strong>
        </div>
        <div className={cls.summaryCard}>
          <span className={cls.summaryTitle}>Current page</span>
          <strong>
            {page} / {meta.totalPages}
          </strong>
        </div>
      </section>

      <section className={cls.panel}>
        <div className={cls.panelHead}>
          <div>
            <h2>Recent messages</h2>
            <p>
              Newest first with sender, receiver, message content, and status.
            </p>
          </div>

          {!loading && !error && rows.length > 0 ? (
            <div className={cls.metaPill}>
              Total: {meta.total} · Page {page} of {meta.totalPages}
            </div>
          ) : null}
        </div>

        {loading && (
          <div className={cls.stateBox}>
            <div className={cls.loader} />
            <p>Loading messages…</p>
          </div>
        )}

        {!loading && error && (
          <div className={`${cls.stateBox} ${cls.errorBox}`}>
            <p className={cls.error}>{error}</p>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className={cls.stateBox}>
            <p>No messages found.</p>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div className={cls.desktopTableWrap}>
              <table className={cls.table}>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Message</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((message) => (
                    <tr key={message.id}>
                      <td>
                        <UserMiniCard
                          user={message.sender}
                          fallbackAlt={message.sender?.username || "Sender"}
                          who={"Sender"}
                        />
                        <UserMiniCard
                          user={message.receiver}
                          fallbackAlt={message.receiver?.username || "Receiver"}
                          who={"Receiver"}
                        />
                      </td>

                      <td>
                      <MediaBlock message={message} accessToken={accessToken} />
                      </td>
                      <td>
                        <MessageStatusBadge status={message.status} />
                        <div className={cls.timeCell}>
                          <span>{formatRelative(message.createdAt)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={cls.mobileList}>
              {rows.map((message) => (
                <article
                  key={`mobile-${message.id}`}
                  className={cls.mobileCard}
                >
                  <div className={cls.mobileTop}>
                    <MessageStatusBadge status={message.status} />
                    <div className={cls.mobileTime}>
                      <strong>{formatDateTime(message.createdAt)}</strong>
                      <span>{formatRelative(message.createdAt)}</span>
                    </div>
                  </div>

                  <div className={cls.mobileUsers}>
                    <div className={cls.mobileUserBlock}>
                      <span className={cls.mobileLabel}>Sender</span>
                      <UserMiniCard
                        user={message.sender}
                        fallbackAlt={message.sender?.username || "Sender"}
                      />
                    </div>

                    <div className={cls.mobileUserBlock}>
                      <span className={cls.mobileLabel}>Receiver</span>
                      <UserMiniCard
                        user={message.receiver}
                        fallbackAlt={message.receiver?.username || "Receiver"}
                      />
                    </div>
                  </div>

                  <div className={cls.mobileMessage}>
                    <span className={cls.mobileLabel}>Message</span>
                    <MediaBlock message={message} accessToken={accessToken} />
                  </div>
                </article>
              ))}
            </div>

            <div className={cls.pagination}>
              <button
                type="button"
                onClick={() => fetchMessages(page - 1)}
                disabled={!meta.hasPrevPage || loading}
              >
                ← Previous
              </button>

              <span className={cls.pageIndicator}>
                Page {page} of {meta.totalPages}
              </span>

              <button
                type="button"
                onClick={() => fetchMessages(page + 1)}
                disabled={!meta.hasNextPage || loading}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
